import type { SupportedSript } from '@meeseeks/centaur-tracking'
import type { Ref } from '@vue/reactivity'
// Reference for this implementation
import { checkIsWebView } from '@camelot/webview-utils'
import { Tracking } from '@meeseeks/centaur-tracking'

// https://gitlab.share-now.com/cuba/barracuda/customer-onboarding-ui/-/blob/main/src/composable/useConsentManagement.ts?ref_type=heads
import type { MaybeRef } from 'vue'
import { computed } from 'vue'

import {
  addScriptElement,
  buildHotjarScript,
  buildScriptElement,
  isRunningOnIntHost,
} from '~/composables/tracking/tracking-scripts'
import { isRunningOnFree2MoveDomain } from './tracking-scripts'

const isOnFree2Move = computed(() => isRunningOnFree2MoveDomain())

const GOOGLE_TAG_MANAGER_ID = computed<string>(() =>
  isOnFree2Move.value ? 'GTM-NF366VX' : 'GTM-KHQQBLB',
)
const CONSENT_KEY_GTM = 'Google Tag Manager'
const CONSENT_KEY_HOTJAR = 'Hotjar'
const CONSENT_TYPES = [CONSENT_KEY_GTM, CONSENT_KEY_HOTJAR]
type ConsentKeysRequiringReload = typeof CONSENT_KEY_GTM | typeof CONSENT_KEY_HOTJAR

const USERCENTRICS_DEFAULT_ID_WEB = computed<string>(() =>
  isOnFree2Move.value ? 'q6vL_KT2u3vGux' : 'O9vNYWD7')
const USERCENTRICS_DEFAULT_ID_APP = 'eZ3L_3LSk'
const USERCENTRICS_TEST_ID = 'PUpBkljw3'
const USERCENTRICS_IDS: Record<number, string> = {
  32: 'jwbPyDd2q', // 🇭🇺
  37: '7Y7k8b0Z3', // 🇩🇰
}

// To prevent adding the same scripts again
const isInitialisedForId = ref<string | null>(null)

/**
 * Wraps the Usercentrics consent management and tracking script integrations.
 *  - Usercentrics API docs - https://docs.usercentrics.com/#/cmp-v2-ui-api
 *  - @meeseeks/centaur-tracking - https://gitlab.share-now.com/meeseeks/manicorn-tracking
 */
export function useConsentManagement(legalEntityId: MaybeRef<number>) {
  const legalEntityIdRef = ref(legalEntityId)
  const isGTMConsentGiven = ref<boolean>(false)
  const isHotjarConsentGiven = ref<boolean>(false)

  async function onConsentChanged() {
    const consent = await getConsent()
    checkAndReloadIfConsentNotGiven(isHotjarConsentGiven, consent, CONSENT_KEY_HOTJAR)
    checkAndReloadIfConsentNotGiven(isGTMConsentGiven, consent, CONSENT_KEY_GTM)
  }

  function onUsercentricsInitialized() {
    // get initial consent
    onConsentChanged()

    // set language for Banner
    window.__ucCmp.changeLanguage(useTranslations().language.value)

    window.removeEventListener('UC_UI_INITIALIZED', onUsercentricsInitialized)
    window.addEventListener('UC_UI_CMP_EVENT', (e: any) => {
      // We are only interested in any event that can update the consent
      // https://docs.usercentrics.com/#/v2-events?id=uc_ui_cmp_event
      if (['ACCEPT_ALL', 'DENY_ALL', 'SAVE'].includes(e.detail.type))
        onConsentChanged()
    })
  }

  function buildScripts(usercentricsId: string): SupportedSript[] {
    /* Create tracking scripts to be embedded using meeseeks library.
    * usercentrics config has to come before Google Analytics in TrackingScriptsConfig
    * See: https://gitlab.share-now.com/meeseeks/manicorn-tracking#usage
    */
    return new Tracking().toObjects({
      usercentrics: {
        ucId: usercentricsId,
      },
      gtm: {
        analyticsId: GOOGLE_TAG_MANAGER_ID.value,
      },
      scripts: {
        hotjar: {
          ucServiceName: 'Hotjar',
          innerHTML: getHotjarScript(),
        },
      },
    })
  }

  function initialize(legalEntityId = 0) {
    const usercentricsId = getUsercentricsId(legalEntityId)
    if (isInitialisedForId.value === usercentricsId)
      return

    const scriptsToAttach = buildScripts(usercentricsId)

    // attach scripts to document head
    scriptsToAttach.forEach((scriptObject: SupportedSript) => {
      const script = buildScriptElement(scriptObject)
      addScriptElement(script)
    })

    // Mark as initialised
    isInitialisedForId.value = usercentricsId || null

    if (window.__ucCmp?.isInitialized())
      onUsercentricsInitialized()
    else
      window.addEventListener('UC_UI_INITIALIZED', onUsercentricsInitialized)
  }

  initialize(legalEntityIdRef.value)
  watch(legalEntityIdRef, () => location.reload())

  return {
    isGTMConsentGiven: readonly(isGTMConsentGiven),
  }
}

function getUsercentricsId(legalEntityId: number = 0): string {
  if (!isRunningOnIntHost())
    return USERCENTRICS_IDS[legalEntityId] || checkIsWebView(window) ? USERCENTRICS_DEFAULT_ID_APP : USERCENTRICS_DEFAULT_ID_WEB.value
  else
    return USERCENTRICS_IDS[legalEntityId] || USERCENTRICS_TEST_ID
}

async function getConsent(): Promise<Record<string, boolean>> {
  if (!window?.__ucCmp?.getConsentDetails)
    return {}

  const { services } = await window.__ucCmp.getConsentDetails()
  return Object.values(services).reduce(
    (acc, { name, consent }) =>
      CONSENT_TYPES.includes(name) ? { ...acc, [name]: consent.given } : acc,
    {},
  )
}

function getHotjarScript(): string {
  const config = useRuntimeConfig().public
  return buildHotjarScript(`${config.hotjarId}`)
}

function checkAndReloadIfConsentNotGiven(
  isConsentGiven: Ref<boolean>,
  consent: Record<string, boolean> | undefined,
  consentKey: ConsentKeysRequiringReload,
): void {
  const isConsentGivenForKey = (consent?.[`${consentKey}`] ?? false)
  if (isConsentGiven.value && !isConsentGivenForKey) {
    // Reload the page if consent is not given since it's the only way to remove the script from the page
    location.reload()
  }
  else {
    isConsentGiven.value = isConsentGivenForKey
  }
}
